// ABOUTME: Read-only news article viewer modal.
// ABOUTME: Renders a saved article with header image, title, and content sections.

import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import PublishIcon from "@mui/icons-material/Publish";
import Shbutton from "../../../theme/buttons/Shbutton";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "../../../../theme";
import SHModalBottom from "../../../theme/Modal/SHModalBottom";
import ReadOnlyTiptap from "../../../theme/chat/ReadOnlyTiptap";
import NewsCarouselButtons from "./components/NewsCarouselButtons";

const MViewNewsArticle = ({ open, handleClose, article, onPublish, onNext, onBack, nextIsLast }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (!article) return null;

    return (
        <SHModalBottom
            open={open}
            onClose={handleClose}
            maxWidth={720}
            centered
        >

            {/* Article content — crossfades between carousel items */}
            <AnimatePresence mode="popLayout" initial={false}>
            <Box
                key={article.id}
                component={motion.div}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ layout: { duration: 0.45, ease: [0.32, 0.72, 0, 1] }, opacity: { duration: 0.32, ease: [0.32, 0.72, 0, 1] } }}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    overflow: "auto",
                    display: "flex",
                    flexDirection: "column",
                    pb: ((article.status !== "published" && onPublish) || onBack || onNext) ? 7 : 0,
                }}
            >

                {/* Header image */}
                {(article.headerImage?.url || article.headerImage?.previewUrl) && (
                    <Box
                        component="img"
                        src={article.headerImage.url || article.headerImage.previewUrl}
                        alt=""
                        sx={{
                            // maxHeight:250,
                            width: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                )}

                <Box
                    sx={{
                        p: 4,
                        pt:3,
                        display: "flex",
                        flexDirection: "column",

                    }}
                >
                    {/* Title */}
                    <Box
                        sx={{
                            fontSize: 22,
                            fontWeight: 900,
                            fontFamily: "Roboto, sans-serif",
                            lineHeight: 1.3,
                            color: colors.txt["primary"],
                            mb: 1.5,
                        }}
                    >
                        {article.title}
                    </Box>

                    {/* Sections */}
                    {article.sections?.map((section, index) => {
                        const prevSection = article.sections[index - 1];
                        const isAfterImage = prevSection?.type === "image";
                        const isImage = section.type === "image";
                        const isImageAfterText = isImage && prevSection?.type === "text";

                        const getMargin = () => {
                            if (isImageAfterText) return 3;
                            if (isImage || isAfterImage) return 3;
                            return 0.5;
                        };

                        return (
                            <Box
                                key={`${article.id}-${section.id}`}
                                sx={{
                                    mt: getMargin(),
                                }}
                            >
                                {section.type === "text" && section.content && (
                                    <ReadOnlyTiptap key={`text-${article.id}-${section.id}`} content={section.content} colors={colors} />
                                )}
                                {section.type === "image" && (section.content?.url || section.content?.previewUrl) && (
                                    <Box
                                        component="img"
                                        src={section.content.url || section.content.previewUrl}
                                        alt=""
                                        sx={{
                                            width: "100%",
                                            // aspectRatio: "16/9",
                                            objectFit: "cover",
                                            borderRadius: 3,
                                            display: "block",
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            </AnimatePresence>

            {/* Floating publish button for drafts */}
            {article.status !== "published" && onPublish && (
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 24,
                        right: 24,
                        zIndex: 10,
                    }}
                >
                    <Shbutton
                        color="green"
                        variant="contained"
                        onClick={() => onPublish(article)}
                        startIcon={<PublishIcon />}
                    >
                        {t("publish") || "Publiceren"}
                    </Shbutton>
                </Box>
            )}

            <NewsCarouselButtons onNext={onNext} onBack={onBack} nextIsLast={nextIsLast} />
        </SHModalBottom>
    );
};

export default MViewNewsArticle;
